import { RealtimeService } from '@shared/services/realtime.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, inject, OnInit, PLATFORM_ID, computed, signal, effect, untracked } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MaterialModule } from '@app/material.module';
import { FormsModule } from '@angular/forms';
import { debounceTime, filter } from 'rxjs/operators';

import { AuthenticationService } from '@shared/services/auth/authentication.service';
import { UsersService } from '@shared/services/entities/users.service';
import { RolesService } from '@shared/services/entities/roles.service';
import { ModulesService } from '@shared/services/modules.service';
import { SupportTicketsService } from '@shared/services/entities/supportTickets.service';
import { Role } from '@shared/models/entities/roles.model';
import { ModuleModel } from '@shared/models/entities/module.model';
import { CustomSnackBarService } from '@app/shared/components/custom-snack-bar/custom-snack-bar.service';
import { ProfileDialogComponent } from '@app/profile-dialog/profile-dialog.component';
import { sortAlphabetically } from '@shared/utils/sort.utils';
import { catalogueTitle } from '@app/catalogues/catalogue-titles';

interface SidebarItem {
    id:       string;
    label:    string;
    icon:     string;
    route:    string;
    disabled: boolean;
    /** Sub-grupo dentro de un grupo (p. ej. "Inventario" en Inventario y logística):
     *  el item no navega, solo despliega estas opciones. */
    children?: SidebarItem[];
}

/**
 * Un grupo expandible en el sidebar (ej. "Inventarios" que agrupa
 * los 4 módulos de stock). Solo aparece cuando hay 2+ hijos.
 */
interface SidebarGroup {
    kind:     'group';
    id:       string;       // identifier estable, e.g. 'inventarios'
    label:    string;       // ej. "Inventarios"
    icon:     string;       // material icon
    children: SidebarItem[];
}

/**
 * Item plano del sidebar (Inicio, Catálogos, un solo hijo de un grupo
 * sin agrupar, etc.).
 */
interface SidebarLeaf extends SidebarItem {
    kind: 'leaf';
    /**
     * Sub-opciones desplegables de un leaf que TAMBIÉN navega (Catálogos):
     * el item sigue llevando a su ruta y una flecha despliega los hijos.
     */
    children?: SidebarItem[];
}

type SidebarEntry = SidebarLeaf | SidebarGroup;

/**
 * Definición de los grupos del sidebar.
 *
 * Un grupo agrupa visualmente todos los módulos cuya `route` empiece con
 * `prefix`. La regla:
 *  - 0 módulos del grupo → el grupo no aparece
 *  - 1 módulo del grupo → el módulo aparece directo, top-level, sin
 *    agruparlo (con su label original).
 *  - 2+ módulos → aparece el padre del grupo, expandible.
 *
 * Para agregar un grupo nuevo en el futuro: añadir una entrada aquí. No
 * requiere tocar HTML ni SCSS.
 */
const SIDEBAR_GROUPS: ReadonlyArray<{
    id:     string;
    label:  string;
    icon:   string;
    prefix: string;   // siempre con slash inicial, sin slash final
    /**
     * Prefijos redundantes que se recortan del label del hijo cuando el grupo
     * SÍ se muestra agrupado. Ej.: dentro de "Inventarios", el módulo
     * "Inventario de productos" se lee simplemente "Productos".
     *
     * Se evalúan en orden, así que van del más largo al más corto.
     * Si el grupo se degrada a leaf (1 solo hijo) el label original se
     * respeta tal cual, porque ahí sí hace falta el contexto.
     */
    childLabelPrefixes?: ReadonlyArray<string>;
}> = [];

/**
 * Orden y agrupación del menú (lo pidió el cliente). Cada módulo se ubica por
 * el primer segmento de su ruta; solo aparece lo que el rol tiene asignado.
 *  - Un grupo con 1 solo módulo visible se muestra como ese módulo, suelto.
 *  - Dentro de un grupo, Catálogos / Salida de material / Auditoría llevan a
 *    su menú de tarjetas (sin desplegar sus sub-módulos, para no anidar 3 niveles).
 *  - Lo que no está aquí va al final, en orden alfabético.
 */
/** Sub-grupo dentro de un grupo: cada opción con su etiqueta corta. */
type MenuLayoutSub = { sub: string; label: string; icon: string; items: { route: string; label: string }[] };

type MenuLayoutItem =
    | { kind: 'leaf'; route: string }
    | { kind: 'group'; id: string; label: string; icon: string; routes: Array<string | MenuLayoutSub> };

const MENU_LAYOUT: ReadonlyArray<MenuLayoutItem> = [
    { kind: 'leaf', route: 'schedule' },
    { kind: 'group', id: 'operacion', label: 'Operación', icon: 'medical_services',
      routes: ['interventions', 'incidents'] },
    { kind: 'group', id: 'logistica', label: 'Inventario y logística', icon: 'inventory_2',
      routes: [
        { sub: 'inventarios', label: 'Inventario', icon: 'inventory', items: [
            { route: 'inventoryCedis',         label: 'SAP Consigna' },
            { route: 'inventorySapPlanta',     label: 'SAP Planta' },
            { route: 'inventoryInstrumentals', label: 'Instrumental' },
            { route: 'inventoryEquipments',    label: 'Equipo' },
            { route: 'inventory',              label: 'Insumos' },
        ] },
        'materialReception', 'materialRequests', 'transferRequests', 'transfers', 'custody'] },
    { kind: 'leaf', route: 'reports' },
    { kind: 'group', id: 'administracion', label: 'Administración', icon: 'admin_panel_settings',
      routes: ['audit', 'catalogues', 'contracts', 'modules', 'permissions', 'roles', 'users'] },
    { kind: 'leaf', route: 'support' },
];

/**
 * Módulos de Medtronic Surgical.
 *
 * El sidebar se arma con la tabla `modules` + el `navAccessTree` del rol (igual
 * que Biomédica). Mientras esas tablas no estén sembradas para Medtronic, esta
 * lista evita que el menú quede vacío.
 */
const FALLBACK_MODULES: ReadonlyArray<SidebarLeaf> = [
    { kind: 'leaf', id: 'catalogues',             label: 'Catálogos',                  icon: 'menu_book',        route: '/catalogues',             disabled: false },
    { kind: 'leaf', id: 'interventions',          label: 'Salida de material',         icon: 'medical_services', route: '/interventions',          disabled: false },
    { kind: 'leaf', id: 'materialReception',      label: 'Entradas de delivery',       icon: 'local_shipping',   route: '/materialReception',      disabled: false },
    { kind: 'leaf', id: 'incidents',              label: 'Incidencias',                icon: 'report_problem',   route: '/incidents',              disabled: false },
    { kind: 'leaf', id: 'materialRequests',       label: 'Solicitud de material',      icon: 'playlist_add',     route: '/materialRequests',       disabled: false },
    { kind: 'leaf', id: 'transfers',              label: 'Traspaso de material',       icon: 'swap_horiz',       route: '/transfers',              disabled: false },
    { kind: 'leaf', id: 'custody',                label: 'Resguardo y devolución',     icon: 'shield',           route: '/custody',                disabled: false },
    { kind: 'leaf', id: 'inventory',              label: 'Inventario de insumos',    icon: 'inventory_2',      route: '/inventory',              disabled: false },
    { kind: 'leaf', id: 'inventoryCedis',         label: 'Inventario SAP Consigna',    icon: 'warehouse',        route: '/inventoryCedis',         disabled: false },
    { kind: 'leaf', id: 'inventorySapPlanta',     label: 'Inventario SAP Planta',      icon: 'factory',          route: '/inventorySapPlanta',     disabled: false },
    { kind: 'leaf', id: 'transferRequests',       label: 'Solicitud de traspaso',      icon: 'move_down',        route: '/transferRequests',       disabled: false },
    { kind: 'leaf', id: 'inventoryInstrumentals', label: 'Inventario de instrumental', icon: 'handyman',         route: '/inventoryInstrumentals', disabled: false },
    { kind: 'leaf', id: 'inventoryEquipments',    label: 'Inventario de equipo',       icon: 'biotech',          route: '/inventoryEquipments',    disabled: false },
    { kind: 'leaf', id: 'reports',                label: 'Reportes',                   icon: 'assessment',       route: '/reports',                disabled: false },
    { kind: 'leaf', id: 'modules',                label: 'Módulos',                    icon: 'widgets',          route: '/modules',                disabled: false },
    { kind: 'leaf', id: 'permissions',            label: 'Permisos',                   icon: 'lock',             route: '/permissions',            disabled: false },
    { kind: 'leaf', id: 'roles',                  label: 'Roles',                      icon: 'badge',            route: '/roles',                  disabled: false },
    { kind: 'leaf', id: 'support',                label: 'Soporte',                    icon: 'support_agent',    route: '/support',                disabled: false },
    { kind: 'leaf', id: 'users',                  label: 'Usuarios',                   icon: 'group',            route: '/users',                  disabled: false },
];

/**
 * Módulos que siguen existiendo en el código y en el router, pero que
 * de momento NO se muestran en el menú:
 *
 *   /schedule    Agenda
 *   /contracts   Contratos
 *   /audit       Auditoría
 *
 * Para volver a mostrarlos basta con agregar su entrada a
 * `FALLBACK_MODULES` (o sembrarlos en la tabla `modules` y en el
 * `navAccessTree` del rol, que es lo que manda cuando hay datos en BD).
 */

@Component({
    selector: 'app-layout',
    standalone: true,
    imports: [MaterialModule, FormsModule, RouterOutlet],
    templateUrl: './layout.component.html',
    styleUrl: './layout.component.scss',
})
export class LayoutComponent implements OnInit {
    private authService    = inject(AuthenticationService);
    private usersService   = inject(UsersService);
    private rolesService   = inject(RolesService);
    private modulesService = inject(ModulesService);
    private dialog         = inject(MatDialog);
    private router         = inject(Router);
    private snackBar       = inject(CustomSnackBarService);
    private platformId     = inject(PLATFORM_ID);
    private isBrowser      = isPlatformBrowser(this.platformId);

    appVersion = 'v2.1.0';
    isLoggingOut = false;

    // ─── Tracking de la ruta actual ────────────────────────────────────────
    currentRoute = signal<string>('/dashboard');

    /** ¿Estamos en el tablero? (regla del producto: ahí el sidebar va ancho) */
    readonly isHomeRoute = computed(() => {
        const r = this.currentRoute();
        return r === '/dashboard' || r === '/' || r.startsWith('/dashboard?') || r.startsWith('/dashboard#');
    });

    /** ¿El usuario está haciendo hover en el sidebar colapsado? */
    isHovering = signal<boolean>(false);

    /**
     * El sidebar se ve "expandido" (260px de ancho) si:
     *  - Estamos en /home, O
     *  - El usuario está haciendo hover en el sidebar (en cualquier otra ruta)
     *
     * Si NO se cumple ninguna, queda en 70px (colapsado, solo iconos).
     */
    readonly isExpanded = computed(() =>
        this.isHomeRoute() || this.isHovering()
    );

    // ─── Datos del usuario / rol ───────────────────────────────────────────
    userName  = computed(() => this.usersService.currentUser()?.name ?? 'Usuario');
    userImage = computed(() => this.usersService.currentUser()?.image ?? null);

    userRole = signal<Role | null>(null);
    userRoleName = computed(() => this.userRole()?.name ?? 'Sin rol');

    // ─── Módulos del sistema ───────────────────────────────────────────────
    allModules = signal<ModuleModel[]>([]);
    /** Rutas de módulos DESACTIVADOS: se ocultan también del menú de respaldo. */
    inactiveRoutes = signal<Set<string>>(new Set());

    /** Al activar/desactivar un módulo, el menú se recarga solo. */
    private readonly reloadOnModuleChange = effect(() => {
        const rev = this.modulesService.revision();
        if (rev > 0) untracked(() => { void this.loadModules(); });
    });

    /** Items visibles del sidebar (Inicio + módulos del rol). */

    /**
     * Lista visible del sidebar. Mezcla leaves y groups según la regla:
     *  - 0 hijos del grupo → grupo no aparece
     *  - 1 hijo            → hijo aparece como leaf (sin agrupar)
     *  - 2+ hijos          → grupo aparece como contenedor expandible
     */
    /**
     * `false` mientras se cargan el usuario, su rol y los módulos. Mientras
     * tanto el menú va vacío y se muestra la pantalla de carga: así nunca se
     * ve el menú de respaldo (o uno equivocado) antes de conocer el rol.
     */
    readonly menuReady = signal(false);

    // ── Notificaciones: tickets de soporte asignados a mí y sin solucionar ──
    private readonly supportTicketsService = inject(SupportTicketsService);
    readonly ticketNotifs = signal<{ id: string; folio: string; subject: string; status: string; priority?: string | null }[]>([]);
    private notifTimer: any = null;
    private readonly realtime = inject(RealtimeService);
    private readonly destroyRef = inject(DestroyRef);

    async loadTicketNotifs(): Promise<void> {
        const me = this.usersService.currentUser()?.id;
        if (!me) { this.ticketNotifs.set([]); return; }
        try {
            const resp: any = await this.supportTicketsService.search({
                searchType: 'default', condition: { deleted: false, assignedTo: me },
            });
            const rows = ((resp?.data ?? []) as any[])
                .filter(t => t && t.assignedTo === me && String(t.status ?? '').toUpperCase() !== 'SOLUCIONADO')
                .map(t => ({ id: t.id, folio: t.folio, subject: t.subject, status: t.status, priority: t.priority }));
            this.ticketNotifs.set(rows);
        } catch {
            /* sin notificaciones si falla */
        }
    }

    openTicketsFromNotif(): void {
        this.router.navigate(['/support/tickets']);
    }
    /** La carga inicial está tardando más de lo normal (se ofrece reintentar). */
    readonly loadingSlow = signal(false);

    reloadApp(): void {
        window.location.reload();
    }

    visibleModules = computed<SidebarEntry[]>(() => {
        if (!this.menuReady()) return [];
        const role = this.userRole();
        const all  = this.allModules();

        const home: SidebarLeaf = {
            kind: 'leaf',
            id: '__home', label: 'Tablero', icon: 'dashboard', route: '/dashboard', disabled: false,
        };

        // Sin rol/módulos en BD todavía: mostramos el catálogo local de módulos
        // para que la app siga siendo navegable.
        if (!role || !role.navAccessTree?.length || all.length === 0) {
            const inactive = this.inactiveRoutes();
            return [home, ...this.arrangeMenu(FALLBACK_MODULES.filter(m => !inactive.has(this.normRoute(m.route))).map(m => ({ ...m })))];
        }

        // Módulos top-level (rutas de 1 segmento) que el rol tiene asignados.
        const topLevelModules = all
            .filter(m => role.navAccessTree.includes(m.id!))
            .filter(m => m.active !== false)
            .filter(m => this.isTopLevelRoute(m.route));

        // Particionar en: candidatos a agrupar (vs. items que se quedan sueltos)
        const groupedByPrefix = new Map<string, SidebarItem[]>();
        const ungrouped: SidebarLeaf[] = [];

        for (const m of topLevelModules) {
            const matchedGroup = SIDEBAR_GROUPS.find(g =>
                this.routeMatchesGroupPrefix(m.route, g.prefix)
            );

            const item: SidebarItem = {
                id:       m.id!,
                label:    m.label || m.name || '',
                icon:     m.icon || 'widgets',
                route:    m.route || '/',
                disabled: false,
            };

            if (matchedGroup) {
                const list = groupedByPrefix.get(matchedGroup.id) ?? [];
                list.push(item);
                groupedByPrefix.set(matchedGroup.id, list);
            } else {
                ungrouped.push({ kind: 'leaf', ...item });
            }
        }

        // Catálogos: además de llevar al menú de catálogos, despliega (con la
        // flecha) los catálogos que el rol tiene asignados, con el mismo nombre
        // que la tarjeta y la pantalla (catalogue-titles.ts).
        const cataloguesLeaf = ungrouped.find(l => this.routeKeyOf(l.route) === 'catalogues');
        if (cataloguesLeaf) {
            const subs: SidebarItem[] = all
                .filter(m => role.navAccessTree.includes(m.id!))
                .filter(m => m.active !== false)
                .filter(m => this.isCatalogueChildRoute(m.route))
                .map(m => ({
                    id:       m.id!,
                    label:    (m.label || m.name || '').trim() || catalogueTitle(m.route, ''),
                    icon:     m.icon || 'layers',
                    route:    m.route || '/',
                    disabled: false,
                }))
                .sort((a, b) => a.label.localeCompare(b.label, 'es'));
            if (subs.length > 0) cataloguesLeaf.children = subs;
        }

        // Salida de material y Auditoría: igual que Catálogos, entran a su
        // menú y con la flecha despliegan sus sub-módulos (/<padre>/<x>).
        for (const parentKey of ['interventions', 'audit']) {
          const parentLeaf = ungrouped.find(l => this.routeKeyOf(l.route) === parentKey);
          if (!parentLeaf) continue;
          {
            const subs: SidebarItem[] = all
                .filter(m => role.navAccessTree.includes(m.id!))
                .filter(m => m.active !== false)
                .filter(m => this.isChildRouteOf(m.route, parentKey) && !/\/menu\/?$/.test(m.route ?? ''))
                .map(m => ({
                    id:       m.id!,
                    label:    (m.label || m.name || '').trim(),
                    icon:     m.icon || 'layers',
                    route:    m.route || '/',
                    disabled: false,
                }))
                .sort((a, b) => a.label.localeCompare(b.label, 'es'));
            if (subs.length > 0) parentLeaf.children = subs;
          }
        }

        // Ahora aplicar la regla de "1 → leaf, 2+ → grupo"
        const groupEntries: SidebarEntry[] = [];
        for (const g of SIDEBAR_GROUPS) {
            const children = groupedByPrefix.get(g.id) ?? [];
            if (children.length === 0) continue;

            if (children.length === 1) {
                // Un solo hijo: lo mostramos top-level con su label original
                groupEntries.push({ kind: 'leaf', ...children[0] });
            } else {
                // Dentro del grupo el label del padre ya dice "Inventarios",
                // así que los hijos solo dicen qué inventario son:
                // "Inventario de productos" → "Productos".
                const shortened = children.map(c => ({
                    ...c,
                    label: this.shortenChildLabel(c.label, g.childLabelPrefixes),
                }));

                groupEntries.push({
                    kind:     'group',
                    id:       g.id,
                    label:    g.label,
                    icon:     g.icon,
                    children: shortened.sort((a, b) => a.label.localeCompare(b.label, 'es')),
                });
            }
        }

        // Mezclar leaves "ungrouped" + grupos en una sola lista ordenada
        // alfabéticamente por su label. Esto hace que un grupo (ej. "Inventarios")
        // aparezca entre "Inicio" y "Módulos" en lugar de quedar al final.
        // "Inicio" siempre va primero, fuera del orden alfabético.
        return [home, ...this.arrangeMenu([...ungrouped, ...groupEntries])];
    });

    /** Acomoda los módulos visibles según MENU_LAYOUT (orden y grupos). */
    private arrangeMenu(entries: SidebarEntry[]): SidebarEntry[] {
        const leaves: SidebarLeaf[] = [];
        for (const e of entries) {
            if (this.isGroup(e)) leaves.push(...e.children.map(c => ({ kind: 'leaf' as const, ...c })));
            else leaves.push(e);
        }
        const byKey = new Map<string, SidebarLeaf>();
        for (const l of leaves) {
            const k = this.routeKeyOf(l.route).toLowerCase();
            if (k && !byKey.has(k)) byKey.set(k, l);
        }
        const used = new Set<string>();
        const take = (route: string): SidebarLeaf | null => {
            const k = route.toLowerCase();
            const l = byKey.get(k);
            if (!l || used.has(k)) return null;
            used.add(k);
            return l;
        };
        const out: SidebarEntry[] = [];
        for (const item of MENU_LAYOUT) {
            if (item.kind === 'leaf') {
                const l = take(item.route);
                if (l) out.push(l);
                continue;
            }
            const kids: SidebarItem[] = [];
            for (const r of item.routes) {
                if (typeof r === 'string') {
                    const l = take(r);
                    if (l) { const { kind, children, ...c } = l; kids.push(c); }
                    continue;
                }
                // Sub-grupo (Inventario): opciones con su etiqueta corta.
                const subs: SidebarItem[] = [];
                for (const it of r.items) {
                    const l = take(it.route);
                    if (l) { const { kind, children, ...c } = l; subs.push({ ...c, label: it.label }); }
                }
                if (subs.length === 1) kids.push({ ...subs[0], label: r.label + ' · ' + subs[0].label });
                else if (subs.length > 1) kids.push({ id: item.id + '-' + r.sub, label: r.label, icon: r.icon, route: '', disabled: false, children: subs });
            }
            if (kids.length === 0) continue;
            if (kids.length === 1 && !kids[0].children?.length) { out.push({ kind: 'leaf', ...kids[0] }); continue; }
            out.push({ kind: 'group', id: item.id, label: item.label, icon: item.icon, children: kids });
        }
        const rest = leaves.filter(l => !used.has(this.routeKeyOf(l.route).toLowerCase()))
            .sort((a, b) => a.label.localeCompare(b.label, 'es'));
        return [...out, ...rest];
    }

    /**
     * Override manual del usuario sobre el estado expandido del grupo.
     * Mapa: groupId → true (expandido manualmente) | false (colapsado manualmente).
     * Si NO existe la entrada, el grupo usa su default basado en la ruta actual:
     *   - Si la ruta actual es un hijo → expandido por default
     *   - Si no → colapsado por default
     *
     * Cuando el usuario clickea el chevron, escribimos el override.
     * Esto permite colapsar manualmente un grupo aunque estés en una de sus
     * rutas hijas.
     */
    groupOverride = signal<Map<string, boolean>>(new Map<string, boolean>());

    /**
     * `true` si el grupo está expandido. Lógica:
     *  1. Si el usuario alternó manualmente → respeta su elección
     *  2. Si no, default = la ruta actual está dentro del grupo
     */
    isGroupExpanded(groupId: string): boolean {
        const override = this.groupOverride().get(groupId);
        if (override !== undefined) return override;
        return this.isCurrentRouteInGroup(groupId);
    }

    /** Leaf con hijos (Catálogos): abierto si el usuario lo abrió o si estás
     *  dentro de uno de sus catálogos. */
    isLeafExpanded(entry: SidebarLeaf): boolean {
        const override = this.groupOverride().get(entry.id);
        if (override !== undefined) return override;
        return (entry.children ?? []).some(c => this.isActive(c.route));
    }

    /** La flecha despliega/cierra sin navegar (el resto del item sí navega). */
    toggleLeaf(entry: SidebarLeaf, event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        const next = new Map(this.groupOverride());
        next.set(entry.id, !this.isLeafExpanded(entry));
        this.groupOverride.set(next);
    }

    private routeKeyOf(route: string | undefined): string {
        return String(route ?? '').split('?')[0].split('#')[0].replace(/^\/+|\/+$/g, '').split('/')[0] ?? '';
    }

    /** `/catalogues/<x>` (sin contar `/catalogues/menu`). */
    /** '/<parent>/<hijo>' (exactamente dos segmentos). */
    private isChildRouteOf(route: string | undefined, parent: string): boolean {
        const parts = String(route ?? '').split('?')[0].split('#')[0].replace(/^\/+|\/+$/g, '').split('/').filter(s => !!s);
        return parts.length === 2 && parts[0] === parent;
    }

    private isCatalogueChildRoute(route: string | undefined): boolean {
        const parts = String(route ?? '').split('?')[0].split('#')[0].replace(/^\/+|\/+$/g, '').split('/').filter(s => !!s);
        return parts.length === 2 && parts[0] === 'catalogues' && parts[1] !== 'menu';
    }

    /**
     * `true` si la ruta actual cae dentro del prefix de este grupo.
     * Sirve también para resaltar el header del grupo cuando alguno
     * de sus hijos está activo.
     */
    isCurrentRouteInGroup(groupId: string): boolean {
        const group = this.visibleModules().find(e => e.id === groupId);
        return !!group && this.isGroup(group) && group.children.some(c => this.isItemActive(c));
    }

    /** Item activo (o alguna de sus sub-opciones, si es sub-grupo). */
    isItemActive(item: SidebarItem): boolean {
        return item.children?.length ? item.children.some(c => this.isActive(c.route)) : this.isActive(item.route);
    }

    /** Sub-grupo dentro de un grupo: abierto si el usuario lo abrió o si estás en una de sus opciones. */
    isSubExpanded(item: SidebarItem): boolean {
        const override = this.groupOverride().get(item.id);
        if (override !== undefined) return override;
        return this.isItemActive(item);
    }

    toggleSub(item: SidebarItem): void {
        const next = new Map(this.groupOverride());
        next.set(item.id, !this.isSubExpanded(item));
        this.groupOverride.set(next);
    }

    /**
     * Alterna el estado del grupo. La nueva preferencia del usuario se
     * registra en `groupOverride` aunque vaya en contra del default.
     */
    toggleGroup(groupId: string): void {
        const wasExpanded = this.isGroupExpanded(groupId);
        const next = new Map(this.groupOverride());
        next.set(groupId, !wasExpanded);
        this.groupOverride.set(next);
    }

    /**
     * Type-guard para que el template pueda discriminar entries
     * en `@switch` o `@if`.
     */
    isGroup(entry: SidebarEntry): entry is SidebarGroup {
        return entry.kind === 'group';
    }

    /**
     * Recorta del label del hijo el prefijo redundante que ya aporta el
     * header del grupo, y capitaliza el resultado.
     *
     *   "Inventario de productos"    → "Productos"
     *   "Inventario de instrumental" → "Instrumental"
     *   "Inventario de equipo"       → "Equipo"
     *
     * Si tras recortar no queda nada (ej. el módulo se llama solo
     * "Inventario"), se devuelve el label original para no dejar el item
     * en blanco.
     */
    private shortenChildLabel(label: string, prefixes: ReadonlyArray<string> | undefined): string {
        if (!label || !prefixes?.length) return label;

        for (const prefix of prefixes) {
            if (label.toLowerCase().startsWith(prefix.toLowerCase())) {
                // Quitamos el prefijo y cualquier separador que quede colgando
                const rest = label.slice(prefix.length).replace(/^[\s:–—-]+/, '').trim();
                if (!rest) return label;
                return rest.charAt(0).toLocaleUpperCase('es') + rest.slice(1);
            }
        }

        return label;
    }

    /**
     * `true` si la ruta empieza con el prefijo del grupo, considerada
     * como segmento completo. `'/stock'` matchea `/stock`, `/stockEquipments`
     * y `/stock/foo`. NO matchea `/stockHolm` (sin slash, pero sería raro).
     */
    private routeMatchesGroupPrefix(route: string | undefined, prefix: string): boolean {
        if (!route) return false;
        const normalized = route.toLowerCase().split('?')[0].split('#')[0];
        const lowerPrefix = prefix.toLowerCase();
        if (!normalized.startsWith(lowerPrefix)) return false;
        const rest = normalized.slice(lowerPrefix.length);
        // Acepta exactamente el prefijo, o seguido de un slash, o inmediatamente
        // continuado con caracteres alfanuméricos (caso /stockEquipments).
        return rest === '' || rest.startsWith('/') || /^[A-Za-z0-9]/.test(rest);
    }

    /**
     * Decide si una ruta es de "nivel raíz" (1 segmento) y por lo tanto debe
     * aparecer en el sidebar. Las rutas anidadas (2+ segmentos) son sub-módulos
     * y no se muestran como item directo del sidebar.
     *
     * Ejemplos:
     *   /home                  → true   (1 segmento)
     *   /users                 → true   (1 segmento)
     *   /catalogues            → true   (1 segmento)
     *   /catalogues/menu       → false  (2 segmentos)
     *   /catalogues/warehouses → false  (2 segmentos)
     */
    private isTopLevelRoute(route: string | undefined): boolean {
        if (!route) return false;
        const segments = route
            .split('?')[0]              // ignorar querystring
            .split('#')[0]              // ignorar hash
            .replace(/^\/+|\/+$/g, '')  // quitar slashes inicial/final
            .split('/')
            .filter(s => s.length > 0); // quitar segmentos vacíos
        return segments.length === 1;
    }

    readonly showNoRoleMessage = computed(() => {
        const user = this.usersService.currentUser();
        return !!user && !user.role;
    });

    async ngOnInit(): Promise<void> {
        if (!this.isBrowser) return;

        // Trackear cambios de ruta para resaltar el item activo y el comportamiento home/no-home
        this.currentRoute.set(this.router.url);
        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe(e => {
                this.currentRoute.set(e.urlAfterRedirects);
                // Cuando navegas a otra pantalla, asumimos que el cursor ya no está
                // sobre el sidebar (lo más probable es que clickeaste un item).
                this.isHovering.set(false);
            });

        // Si tarda de más NO se muestra la app sin rol: solo se ofrece reintentar.
        const slow = setTimeout(() => this.loadingSlow.set(true), 15000);
        try {
            if (!this.usersService.currentUser()) {
                try {
                    await this.usersService.loadCurrentUser();
                } catch (err) {
                    console.warn('[Layout] No se pudo cargar el usuario:', err);
                }
            }

            await Promise.all([
                this.loadModules(),
                this.loadUserRole(),
            ]);
        } finally {
            clearTimeout(slow);
            this.loadingSlow.set(false);
            this.menuReady.set(true);
        }
        // Tiempo real: una conexión WebSocket por sesión (módulo MEDTRONIC-SURGICAL).
        // Cada pantalla se suscribe a sus módulos con liveReload().
        this.realtime.start(this.usersService.getOrgId());
        this.destroyRef.onDestroy(() => { this.realtime.stop(); clearInterval(this.notifTimer); });

        // Notificaciones de tickets asignados: al entrar, al haber cambios en
        // tickets y, por si se cae el WebSocket, cada 5 minutos.
        void this.loadTicketNotifs();
        this.realtime.on(['supportTickets']).pipe(debounceTime(1500), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => void this.loadTicketNotifs());
        this.notifTimer = setInterval(() => void this.loadTicketNotifs(), 300000);
    }

    private normRoute(route: string | undefined | null): string {
        return '/' + String(route ?? '').split('?')[0].split('#')[0].replace(/^\/+|\/+$/g, '').toLowerCase();
    }

    private async loadModules(): Promise<void> {
        try {
            const resp: any = await this.modulesService.search({
                searchType: 'default',
                condition: { deleted: false },
            });
            const list: ModuleModel[] = Array.isArray(resp?.data) ? resp.data : [];
            // Un módulo desactivado NO aparece en el menú (ni en el de respaldo).
            this.inactiveRoutes.set(new Set(list.filter(m => m.active === false).map(m => this.normRoute(m.route))));
            this.allModules.set(sortAlphabetically(list.filter(m => m.active !== false), 'label'));
        } catch (err) {
            console.warn('[Layout] No se pudieron cargar los módulos:', err);
            this.allModules.set([]);
        }
    }

    private async loadUserRole(): Promise<void> {
        const user = this.usersService.currentUser();
        if (!user?.role) {
            this.userRole.set(null);
            return;
        }
        try {
            await this.rolesService.search({
                searchType: 'default',
                condition: { id: user.role },
            });
            const roles = this.rolesService.listData();
            this.userRole.set(roles.length > 0 ? (roles[0] as Role) : null);
        } catch (err) {
            console.warn('[Layout] No se pudo cargar el rol:', err);
            this.userRole.set(null);
        }
    }

    /** Considera "activa" la ruta actual o cualquier ruta hija. */
    isActive(route: string): boolean {
        const current = this.currentRoute();
        if (route === '/dashboard') {
            return current === '/dashboard' || current === '/';
        }
        return current === route || current.startsWith(route + '/');
    }

    navigateTo(route: string): void {
        this.router.navigate([route]);
    }

    // ─── Navegación móvil (barra flotante estilo app) ───────────────────────
    //
    // En pantallas chicas el sidebar se oculta y navega esta barra:
    // Inicio (tablero) · Buscar · Menú (hamburguesa con todos los módulos) ·
    // Mi perfil. El buscador filtra las pantallas disponibles y navega — es
    // un lanzador rápido, no busca dentro del contenido de la pantalla.

    mobileMenuOpen = signal<boolean>(false);
    mobileSearchOpen = signal<boolean>(false);
    mobileSearchTerm = signal<string>('');

    /** Todos los módulos visibles, con los grupos aplanados. */
    flatLeaves = computed<SidebarItem[]>(() => {
        const out: SidebarItem[] = [];
        for (const entry of this.visibleModules()) {
            if (this.isGroup(entry)) out.push(...entry.children);
            else out.push(entry);
        }
        return out;
    });

    private static normalizeSearch(text: string): string {
        return (text ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
    }

    filteredLeaves = computed<SidebarItem[]>(() => {
        const q = LayoutComponent.normalizeSearch(this.mobileSearchTerm());
        if (!q) return this.flatLeaves();
        return this.flatLeaves().filter(l => LayoutComponent.normalizeSearch(l.label).includes(q));
    });

    toggleMobileMenu(): void {
        this.mobileSearchOpen.set(false);
        this.mobileMenuOpen.set(!this.mobileMenuOpen());
    }

    toggleMobileSearch(): void {
        this.mobileMenuOpen.set(false);
        this.mobileSearchOpen.set(!this.mobileSearchOpen());
        this.mobileSearchTerm.set('');
    }

    onMobileSearchInput(event: Event): void {
        this.mobileSearchTerm.set((event.target as HTMLInputElement).value ?? '');
    }

    mobileNavigate(route: string | undefined): void {
        this.mobileMenuOpen.set(false);
        this.mobileSearchOpen.set(false);
        if (route) this.navigateTo(route);
    }

    // ─── Hover handlers ─────────────────────────────────────────────────────
    // Solo aplican cuando NO estamos en home (en home el sidebar ya está fijo y
    // ancho; estos eventos se ignoran allí porque isExpanded ya da true).

    onSidebarMouseEnter(): void {
        if (!this.isHomeRoute()) {
            this.isHovering.set(true);
        }
    }

    onSidebarMouseLeave(): void {
        if (!this.isHomeRoute()) {
            this.isHovering.set(false);
        }
    }

    openProfile(): void {
        const ref = this.dialog.open(ProfileDialogComponent, {
            width:        '480px',
            maxWidth:     '95vw',
            disableClose: false,
            autoFocus:    false,
            panelClass:   'profile-dialog-panel',
        });

        ref.afterClosed().subscribe(async (changed: boolean) => {
            if (changed) {
                await this.usersService.loadCurrentUser(true);
                await this.loadUserRole();
            }
        });
    }

    async logout(): Promise<void> {
        if (this.isLoggingOut) return;
        const confirmed = this.isBrowser ? window.confirm('¿Cerrar sesión?') : false;
        if (!confirmed) return;

        this.isLoggingOut = true;
        this.realtime.stop();

        try {
            await this.authService.logOut();
            this.usersService.clearCurrentUser();
            this.snackBar.open('Sesión cerrada correctamente', 'info');
        } catch (err) {
            console.error('Error al cerrar sesión:', err);
            this.usersService.clearCurrentUser();
            this.snackBar.open('Sesión cerrada localmente', 'info');
        } finally {
            this.isLoggingOut = false;
            this.router.navigate(['/'], { replaceUrl: true });
        }
    }
}